import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Skeleton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getReadingStats } from "../../bible/readingHistoryStorage";
import { AuthContext } from "../../firebase/authContext";

const ReadingStatsCard = ({ bibleChapters, edition }) => {
  const auth = useContext(AuthContext);
  const waitingForAuthoritative = Boolean(auth && !auth.readingReady);
  const isEn = edition?.id === "en";
  const [stats, setStats] = useState(() =>
    waitingForAuthoritative ? null : getReadingStats(bibleChapters)
  );

  const syncStats = useCallback(() => {
    setStats(getReadingStats(bibleChapters));
  }, [bibleChapters]);

  useEffect(() => {
    if (waitingForAuthoritative) {
      setStats(null);
      return undefined;
    }
    syncStats();
    window.addEventListener("bible_read_status_changed", syncStats);
    return () => {
      window.removeEventListener("bible_read_status_changed", syncStats);
    };
  }, [syncStats, waitingForAuthoritative]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.75, sm: 2 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
            {isEn ? "Reading Progress" : "全书读经进度"}
          </Typography>
        </Box>
        {waitingForAuthoritative || !stats ? (
          <Skeleton variant="text" width={36} height={20} />
        ) : (
          <Typography variant="caption" sx={{ fontWeight: 800, color: "primary.main" }}>
            {stats.percent}%
          </Typography>
        )}
      </Box>

      <Box sx={{ mb: 1 }}>
        {waitingForAuthoritative || !stats ? (
          <Skeleton variant="rounded" height={7} sx={{ borderRadius: 4 }} />
        ) : (
          <LinearProgress
            variant="determinate"
            value={stats.percent}
            sx={{
              height: 7,
              borderRadius: 4,
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)",
              "& .MuiLinearProgress-bar": {
                borderRadius: 4,
              },
            }}
          />
        )}
      </Box>

      {waitingForAuthoritative || !stats ? (
        <Box
          data-testid="reading-stats-skeleton"
          aria-busy="true"
          aria-live="polite"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Skeleton variant="text" width="42%" height={18} />
          <Skeleton variant="text" width="36%" height={18} />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {isEn
              ? `${stats.totalRead} of ${stats.totalChapters} chapters completed`
              : `已完成 ${stats.totalRead} / ${stats.totalChapters} 章`}
          </Typography>

          <Typography variant="caption" color="text.secondary">
            {isEn
              ? `OT: ${stats.otRead}/${stats.otTotal} · NT: ${stats.ntRead}/${stats.ntTotal}`
              : `旧约: ${stats.otRead}/${stats.otTotal} · 新约: ${stats.ntRead}/${stats.ntTotal}`}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default ReadingStatsCard;
